import { v4 as uuidv4 } from 'uuid'
import { DesignPackage } from './DesignPackage.js'

/**
 * Converts a legacy file into a fresh DesignPackage (see docs plan "Design
 * Packages" § Import from Plate). Two legacy shapes are accepted — both
 * plain JSONL, one object per line:
 *
 *  - a raw OpenHASP plate export, as pulled directly off a device (or
 *    produced by the firmware-export feature) — no wrapper, first line is
 *    the `{"comment":"Designer Config","plate":{...}}` metadata line.
 *  - a legacy Designer design-save file (from before Design Packages
 *    existed) — a JSON wrapper line (`{versionHistory, guidelines,
 *    bookmarks, datasets}`) followed by the design's own JSONL body.
 *
 * This is a legacy IMPORT PATH only, not a legacy storage format: the
 * result is an ordinary DesignPackage with a freshly-minted id. Once handed
 * to importPackage()/DesignPackageLoader, the design is managed as a
 * package from then on, exactly like anything opened from a .zip — nothing
 * downstream needs to know the source was legacy JSONL.
 *
 * Contract: this module is a pure format converter — text in, DesignPackage
 * out — and stays that way. It must never import Importer/DesignPackageLoader,
 * a resource adapter/store, or anything touching the live layer/editor state;
 * that's importPackage()'s job, called separately by whoever has a file to
 * import (see App.vue's importFromPlate()). Keeping this isolated is what
 * makes it safe to delete outright if OpenHASP ever gains native package
 * support.
 */
export function legacyFileToPackage(rawText, filename) {
    const lines = rawText.split('\n').filter(line => line.trim().length >= 4)

    let versionHistory = []
    let guidelines = []
    let bookmarks = []
    let datasets = []
    let bodyLines = lines

    if (lines.length > 0) {
        let firstLineObj = null
        try {
            firstLineObj = JSON.parse(lines[0])
        } catch {
            firstLineObj = null
        }
        // The legacy design-save wrapper has neither `plate` nor `page`/`id` —
        // just the session-state fields — distinguishing it from a raw plate
        // export's first line (`{"comment":"Designer Config","plate":{...}}`).
        if (firstLineObj && firstLineObj.plate === undefined && firstLineObj.versionHistory !== undefined) {
            versionHistory = firstLineObj.versionHistory ?? []
            guidelines = firstLineObj.guidelines ?? []
            bookmarks = firstLineObj.bookmarks ?? []
            datasets = firstLineObj.datasets ?? []
            bodyLines = lines.slice(1)
        }
    }

    const objects = []
    for (const line of bodyLines) {
        try {
            objects.push(JSON.parse(line))
        } catch {
            // Tolerate unparsable lines — the old line-based Importer did too.
        }
    }

    const designId = uuidv4()
    const name = (filename || 'design').replace(/\.jsonl$/i, '').replace(/_v\d+(\.\d+)?$/, '')

    const pkg = new DesignPackage()
    pkg.setDesignJson({ id: designId, versionHistory, guidelines, bookmarks, datasets, objects })
    pkg.regenerateManifest({ id: designId, name })
    return pkg
}
